import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Clock, ChevronLeft, ChevronRight, Send, AlertTriangle, CheckCircle, XCircle, MinusCircle, ArrowLeft, RotateCcw, Filter, Trophy } from 'lucide-react';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const getAuthHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('auth_token')}`, 'Content-Type': 'application/json' });

const ExaminationEngine = ({ rankId, year, rankName, onBack, existingSession, existingQuestions }) => {
  const [phase, setPhase] = useState('loading'); // loading, exam, result, review
  const phaseRef = useRef('loading');
  const updatePhase = (p) => { phaseRef.current = p; setPhase(p); };
  const [session, setSession] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [submitDialog, setSubmitDialog] = useState(false);
  const [result, setResult] = useState(null);
  const [reviewData, setReviewData] = useState(null);
  const [reviewFilter, setReviewFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);
  const [explanations, setExplanations] = useState({});
  const [loadingExplanation, setLoadingExplanation] = useState({});
  const timerRef = useRef(null);
  const localSaveRef = useRef({});
  const startedRef = useRef(false);
  const onBackRef = useRef(onBack);
  onBackRef.current = onBack;

  // Start exam — runs exactly once
  const startExam = useCallback(async () => {
    if (startedRef.current) return;
    startedRef.current = true;

    // If a pre-created session (e.g. mock exam) was passed, use it directly
    if (existingSession && existingQuestions) {
      setSession(existingSession);
      setQuestions(existingQuestions);
      if (existingSession.answers) setAnswers(existingSession.answers);
      localSaveRef.current = existingSession.answers || {};
      const expires = new Date(existingSession.expires_at).getTime();
      const remaining = Math.max(0, Math.floor((expires - Date.now()) / 1000));
      setTimeLeft(remaining);
      updatePhase('exam');
      return;
    }
    try {
      const res = await fetch(`${API_BASE}/api/promotion/exams/start`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ rank_id: rankId, year })
      });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail || 'Failed to start'); onBackRef.current(); return; }
      const data = await res.json();
      setSession(data.session);
      setQuestions(data.questions);
      if (data.session.answers) setAnswers(data.session.answers);
      localSaveRef.current = data.session.answers || {};
      const expires = new Date(data.session.expires_at).getTime();
      const remaining = Math.max(0, Math.floor((expires - Date.now()) / 1000));
      setTimeLeft(remaining);
      updatePhase('exam');
    } catch (e) { toast.error('Failed to start examination'); onBackRef.current(); }
  }, [rankId, year, existingSession, existingQuestions]);

  useEffect(() => { startExam(); }, [startExam]);

  // Timer — ONLY runs during exam phase
  useEffect(() => {
    if (phase !== 'exam') {
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      return;
    }
    if (timeLeft <= 0) return;
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          handleAutoSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; } };
    // Intentionally only depends on `phase`; timeLeft/handleAutoSubmit are read via refs/closure.
  }, [phase]);

  const handleAutoSubmit = async () => {
    if (!session || phaseRef.current !== 'exam') return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/api/promotion/exams/${session.id}/submit`, { method: 'POST', headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        updatePhase('result');
        toast.info('Time expired! Exam auto-submitted.');
      }
    } catch (e) { toast.error('Auto-submit failed'); }
    setSubmitting(false);
  };

  // Save answer (autosave)
  const selectAnswer = async (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
    localSaveRef.current[questionId] = option;
    // Async server save
    try {
      await fetch(`${API_BASE}/api/promotion/exams/${session.id}/answer`, {
        method: 'PUT', headers: getAuthHeaders(),
        body: JSON.stringify({ question_id: questionId, selected_option: option })
      });
    } catch (e) { /* Saved locally, will sync later */ }
  };

  // Manual submit
  const handleSubmit = async () => {
    setSubmitDialog(false);
    setSubmitting(true);
    clearInterval(timerRef.current);
    try {
      const res = await fetch(`${API_BASE}/api/promotion/exams/${session.id}/submit`, { method: 'POST', headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setResult(data);
        updatePhase('result');
        toast.success('Examination submitted!');
      } else { const e = await res.json(); toast.error(e.detail || 'Submit failed'); }
    } catch (e) { toast.error('Submission error'); }
    setSubmitting(false);
  };

  // Load review
  const loadReview = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/promotion/exams/${session.id}/review`, { headers: getAuthHeaders() });
      if (res.ok) { setReviewData(await res.json()); updatePhase('review'); }
    } catch (e) { toast.error('Failed to load review'); }
  };

  // Fetch AI explanation for a question (cached on backend)
  const fetchExplanation = async (questionId) => {
    if (explanations[questionId]) return; // already loaded
    setLoadingExplanation(prev => ({ ...prev, [questionId]: true }));
    try {
      const res = await fetch(`${API_BASE}/api/promotion/question/${questionId}/explain`, {
        method: 'POST', headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setExplanations(prev => ({ ...prev, [questionId]: data.explanation }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to get explanation');
      }
    } catch (e) { toast.error('Failed to get explanation'); }
    finally { setLoadingExplanation(prev => ({ ...prev, [questionId]: false })); }
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const answeredCount = Object.keys(answers).length;
  const totalQ = questions.length;
  const currentQ = questions[currentIndex];
  const isUrgent = timeLeft < 300; // < 5 min

  // ===== LOADING =====
  if (phase === 'loading') {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4">
        <div className="animate-spin h-10 w-10 border-4 border-amber-600 border-t-transparent rounded-full" />
        <p className="text-gray-600">Preparing your examination...</p>
      </div>
    );
  }

  // ===== RESULT =====
  if (phase === 'result' && result) {
    const mins = Math.floor((result.time_used_seconds || 0) / 60);
    const secs = (result.time_used_seconds || 0) % 60;
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <Card className="border-2 border-green-200 bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6 sm:p-8 text-center">
            <Trophy className="h-12 w-12 text-amber-500 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-gray-900 mb-1">Examination Completed</h2>
            <p className="text-sm text-gray-500 mb-4">{result.rank_name} — {result.year === 0 ? 'Mock Exam' : result.year}</p>
            <div data-testid="result-percentage" className="text-5xl font-bold text-green-700 mb-2">{result.percentage}%</div>
            <div className="grid grid-cols-3 gap-3 mt-6 text-center">
              <div className="p-3 bg-white rounded-lg shadow-sm">
                <CheckCircle className="h-5 w-5 text-green-600 mx-auto mb-1" />
                <p className="text-lg font-bold text-green-700">{result.correct_count}</p>
                <p className="text-xs text-gray-500">Correct</p>
              </div>
              <div className="p-3 bg-white rounded-lg shadow-sm">
                <XCircle className="h-5 w-5 text-red-500 mx-auto mb-1" />
                <p className="text-lg font-bold text-red-600">{result.wrong_count}</p>
                <p className="text-xs text-gray-500">Wrong</p>
              </div>
              <div className="p-3 bg-white rounded-lg shadow-sm">
                <MinusCircle className="h-5 w-5 text-gray-400 mx-auto mb-1" />
                <p className="text-lg font-bold text-gray-500">{result.unanswered_count}</p>
                <p className="text-xs text-gray-500">Skipped</p>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-4">Time used: {mins}m {secs}s</p>
          </CardContent>
        </Card>
        <div className="flex gap-3 justify-center flex-wrap">
          <Button data-testid="review-answers-btn" onClick={loadReview} className="bg-blue-600 hover:bg-blue-700">Review Answers</Button>
          <Button data-testid="back-to-dashboard-btn" variant="outline" onClick={onBack}><ArrowLeft className="h-4 w-4 mr-1" />Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  // ===== REVIEW =====
  if (phase === 'review' && reviewData) {
    const filtered = reviewData.review.filter(q => {
      if (reviewFilter === 'correct') return q.is_correct;
      if (reviewFilter === 'wrong') return !q.is_correct && !q.is_unanswered;
      if (reviewFilter === 'unanswered') return q.is_unanswered;
      return true;
    });
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-bold text-lg">Answer Review — {reviewData.rank_name} {reviewData.year === 0 ? 'Mock Exam' : reviewData.year}</h3>
          <Button data-testid="back-to-results-btn" variant="outline" size="sm" onClick={() => updatePhase('result')}><ArrowLeft className="h-4 w-4 mr-1" />Results</Button>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[['all', 'All', 'bg-gray-100'], ['correct', 'Correct', 'bg-green-100'], ['wrong', 'Wrong', 'bg-red-100'], ['unanswered', 'Skipped', 'bg-gray-100']].map(([val, label, bg]) => (
            <Button key={val} size="sm" variant={reviewFilter === val ? 'default' : 'outline'} onClick={() => setReviewFilter(val)}
              className={reviewFilter === val ? '' : bg}>{label}
              <Badge variant="secondary" className="ml-1 text-xs">{
                val === 'all' ? reviewData.review.length :
                val === 'correct' ? reviewData.correct_count :
                val === 'wrong' ? reviewData.wrong_count : reviewData.unanswered_count
              }</Badge>
            </Button>
          ))}
        </div>
        <div className="space-y-3">
          {filtered.map((q, i) => (
            <Card key={q.question_id} className={`border-l-4 ${q.is_correct ? 'border-l-green-500' : q.is_unanswered ? 'border-l-gray-300' : 'border-l-red-500'}`}>
              <CardContent className="p-4">
                <p className="font-medium text-sm mb-3">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-gray-200 text-gray-700 text-xs font-bold mr-2 shrink-0">
                    {q.question_number || (i + 1)}
                  </span>
                  {q.question_text}
                </p>
                {/* Multi-choice / True-False options review */}
                {q.question_type !== 'fill_in' && q.options && Object.keys(q.options).length > 0 && (
                  <div className="space-y-1.5">
                    {Object.entries(q.options).map(([key, val]) => {
                      const isUser = q.user_answer === key;
                      const isCorrect = q.correct_answer === key;
                      let cls = 'bg-gray-50 text-gray-600';
                      if (isCorrect) cls = 'bg-green-100 text-green-800 font-medium';
                      if (isUser && !isCorrect) cls = 'bg-red-100 text-red-800 line-through';
                      return (
                        <div key={key} className={`px-3 py-2 rounded-lg text-sm flex items-center gap-2 ${cls}`}>
                          <span className="font-bold w-5">{key}.</span>{val}
                          {isCorrect && <CheckCircle className="h-4 w-4 text-green-600 ml-auto shrink-0" />}
                          {isUser && !isCorrect && <XCircle className="h-4 w-4 text-red-500 ml-auto shrink-0" />}
                        </div>
                      );
                    })}
                  </div>
                )}
                {/* Fill-in review */}
                {q.question_type === 'fill_in' && (
                  <div className="space-y-1.5">
                    <div className={`px-3 py-2 rounded-lg text-sm ${q.is_correct ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      Your answer: <strong>{q.user_answer || '(blank)'}</strong>
                      {q.is_correct ? <CheckCircle className="h-4 w-4 text-green-600 inline ml-2" /> : <XCircle className="h-4 w-4 text-red-500 inline ml-2" />}
                    </div>
                    {!q.is_correct && (
                      <div className="px-3 py-2 rounded-lg text-sm bg-green-100 text-green-800">
                        Correct answer: <strong>{q.correct_answer}</strong>
                      </div>
                    )}
                  </div>
                )}
                {q.explanation && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
                    <span className="font-medium">Explanation:</span> {q.explanation}
                  </div>
                )}
                {/* AI Deep Explanation */}
                <div className="mt-3 border-t pt-3">
                  {explanations[q.question_id] ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-gray-800 leading-relaxed whitespace-pre-line">
                      {explanations[q.question_id]}
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-amber-700 border-amber-300 hover:bg-amber-50 w-full"
                      onClick={() => fetchExplanation(q.question_id)}
                      disabled={loadingExplanation[q.question_id]}
                      data-testid={`explain-btn-${q.question_id}`}
                    >
                      {loadingExplanation[q.question_id] ? (
                        <><span className="h-4 w-4 mr-2 border-2 border-amber-500 border-t-transparent rounded-full animate-spin inline-block" />Researching...</>
                      ) : (
                        <>Confused About The Answer?</>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && <p className="text-center text-gray-500 py-6">No questions match this filter.</p>}
        </div>
      </div>
    );
  }

  // ===== EXAM =====
  if (phase !== 'exam' || !currentQ) return null;

  return (
    <div className="space-y-3">
      {/* Timer + Progress Bar */}
      <div className="sticky top-0 z-40 bg-white border-b shadow-sm -mx-4 px-4 py-2 sm:-mx-6 sm:px-6">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">Q{currentQ.question_number || (currentIndex + 1)} ({currentIndex + 1} of {totalQ})</span>
          <div data-testid="exam-timer" className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-bold ${isUrgent ? 'bg-red-100 text-red-700 animate-pulse' : 'bg-amber-100 text-amber-700'}`}>
            <Clock className="h-4 w-4" />
            {formatTime(timeLeft)}
          </div>
          <span data-testid="answered-count" className="text-xs text-gray-500">{answeredCount}/{totalQ} answered</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1.5">
          <div className="bg-amber-500 h-1.5 rounded-full transition-all" style={{ width: `${(answeredCount / totalQ) * 100}%` }} />
        </div>
      </div>

      {/* Question Card */}
      <Card className="border-2">
        <CardContent className="p-4 sm:p-6">
          <p data-testid="question-text" className="text-base sm:text-lg font-medium text-gray-900 mb-5 leading-relaxed">{currentQ.question_text}</p>

          {/* Multi-choice / True-False options */}
          {currentQ.question_type !== 'fill_in' && currentQ.options && Object.keys(currentQ.options).length > 0 && (
            <div className="space-y-2.5">
              {Object.entries(currentQ.options).map(([key, val]) => {
                const selected = answers[currentQ.id] === key;
                return (
                  <button key={key} data-testid={`option-${key}`} onClick={() => selectAnswer(currentQ.id, key)}
                    className={`w-full text-left p-3.5 sm:p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                      selected ? 'border-amber-500 bg-amber-50 shadow-sm' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <span className={`w-9 h-9 rounded-full flex items-center justify-center font-bold text-sm shrink-0 ${
                      selected ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600'
                    }`}>{key}</span>
                    <span className="text-sm sm:text-base">{val}</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Fill-in-the-gap */}
          {currentQ.question_type === 'fill_in' && (
            <div className="mt-2">
              <input
                type="text"
                data-testid="fill-in-answer"
                value={answers[currentQ.id] || ''}
                onChange={e => {
                  const val = e.target.value;
                  setAnswers(prev => ({ ...prev, [currentQ.id]: val }));
                  localSaveRef.current[currentQ.id] = val;
                }}
                onBlur={() => {
                  const val = answers[currentQ.id] || localSaveRef.current[currentQ.id];
                  if (val && session) {
                    fetch(`${API_BASE}/api/promotion/exams/${session.id}/answer`, {
                      method: 'PUT', headers: getAuthHeaders(),
                      body: JSON.stringify({ question_id: currentQ.id, selected_option: val })
                    }).catch(() => {});
                  }
                }}
                placeholder="Type your answer here..."
                className="w-full p-3.5 rounded-xl border-2 border-gray-200 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none text-base"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between gap-2">
        <Button data-testid="prev-question-btn" variant="outline" size="sm" disabled={currentIndex === 0} onClick={() => setCurrentIndex(i => i - 1)}>
          <ChevronLeft className="h-4 w-4 mr-1" />Prev
        </Button>
        <Button data-testid="next-question-btn" variant="outline" size="sm" disabled={currentIndex === totalQ - 1} onClick={() => setCurrentIndex(i => i + 1)}>
          Next<ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Submit Button */}
      <div className="text-center pb-4">
        <Button data-testid="submit-exam-btn" onClick={() => setSubmitDialog(true)} disabled={submitting} className="bg-green-600 hover:bg-green-700 px-8">
          <Send className="h-4 w-4 mr-2" />Submit Examination
        </Button>
      </div>

      {/* Submit Confirmation Dialog */}
      <Dialog open={submitDialog} onOpenChange={setSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" />Submit Examination?</DialogTitle>
            <DialogDescription>
              You are about to submit your examination.
            </DialogDescription>
          </DialogHeader>
          <div className="py-3 space-y-2 text-sm">
            <p>You have answered <strong>{answeredCount}</strong> of <strong>{totalQ}</strong> questions.</p>
            {totalQ - answeredCount > 0 && (
              <p className="text-amber-600 font-medium">{totalQ - answeredCount} questions remain unanswered.</p>
            )}
            <p className="text-gray-500">After submission, answers cannot be changed.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSubmitDialog(false)}>Cancel</Button>
            <Button data-testid="confirm-submit-btn" onClick={handleSubmit} disabled={submitting} className="bg-green-600 hover:bg-green-700">
              {submitting ? 'Submitting...' : 'Submit Examination'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default React.memo(ExaminationEngine, () => true);
